import type { AudienceRule, Rules } from '@/types/types'

export function useAudienceRulesFormatter() {
  const { t } = useI18n()

  const formatRuleName = (ruleKey: Rules): string => {
    const ruleNameKeys: Record<Rules, string> = {
      registrationDate: 'pages.audience.audienceCreatePage.block.visit.registrationDate.label',
      checkinCount: 'pages.audience.audienceCreatePage.block.visit.checkinCount.label',
      lastValidCheckin: 'pages.audience.audienceCreatePage.block.visit.lastValidCheckin.label',
      pointTotal: 'pages.audience.audienceCreatePage.block.engagement.pointTotal.label',
      pointCumulated: 'pages.audience.audienceCreatePage.block.engagement.pointCumulated.label',
      age: 'pages.audience.audienceCreatePage.block.profil.age.label',
      gender: 'global.gender.name',
      averageCheckinCount: 'pages.audience.audienceCreatePage.block.visit.averageCheckinCount.label',
      consumedProduct: 'pages.audience.audienceCreatePage.block.consumedProduct.product.label',
      contestGameParticipation: 'pages.audience.audienceCreatePage.block.engagement.modules.label',
      country: 'pages.audience.audienceCreatePage.block.profil.country.label',
      frequencyType: 'pages.audience.audienceCreatePage.block.visit.tags.label',
      hasOptin: 'pages.audience.audienceCreatePage.block.profil.optin.label',
      surveys: 'pages.audience.filters.surveys',
      tags: 'pages.audience.audienceCreatePage.block.engagement.tags.label'
    }
    return t(ruleNameKeys[ruleKey] || ruleKey)
  }

  const formatOperator = (operator: string, isDateOrNumber: boolean = true): string => {
    if (isDateOrNumber) {
      const relativeDateOperators: Record<string, string> = {
        eq: 'pages.audience.operators.eq',
        gt: 'pages.audience.operators.date.relatives.gt',
        gte: 'pages.audience.operators.date.relatives.gte',
        lt: 'pages.audience.operators.date.relatives.lt',
        lte: 'pages.audience.operators.date.relatives.lte',
        between: 'pages.audience.operators.between'
      }
      const key = relativeDateOperators[operator] || `pages.audience.operators.${operator}`
      return t(key).toLowerCase()
    }

    return t(`pages.audience.operators.${operator}`, operator)
  }

  const formatValue = (value: any, ruleKey: Rules, operator: string): string => {
    if (Array.isArray(value)) {
      if (operator === 'between' && value.length === 2) {
        return `${value[0]} et ${value[1]}`
      }
      if (ruleKey === 'gender') {
        return value
          .map((v) => {
            const key = ['man', 'woman', 'undisclosed', 'other', 'unknown'].includes(v) ? `global.gender.${v}` : v
            return t(key, v)
          })
          .join(', ')
      }
      if (value.length === 1) {
        return String(value[0])
      }
      return value.join(', ')
    }
    return String(value)
  }

  const formatTimeUnit = (ruleKey: Rules, value: number): string => {
    const dayRules: Rules[] = ['registrationDate', 'lastValidCheckin']

    if (dayRules.includes(ruleKey)) {
      return value === 1 ? t('global.words.day') : t('global.words.days')
    }

    if (ruleKey === 'age') {
      return t('pages.audience.audienceCreatePage.block.profil.age.unit')
    }

    return ''
  }

  const formatDateFilter = (dateFilter: any): string => {
    if (!dateFilter || !dateFilter.value || dateFilter.value.length === 0) {
      return ''
    }

    const value = Array.isArray(dateFilter.value) ? dateFilter.value[0] : dateFilter.value
    const dayUnit = value === 1 ? t('global.words.day') : t('global.words.days')

    if (dateFilter.type === 'relative') {
      if (dateFilter.operator === 'lt') {
        return ` (${t('pages.audience.operators.date.relatives.lt').toLowerCase()} ${value} ${dayUnit})`
      }
      if (dateFilter.operator === 'gte') {
        return ` (${t('pages.audience.operators.date.relatives.gte').toLowerCase()} ${value} ${dayUnit})`
      }
      if (dateFilter.operator === 'gt') {
        return ` (${t('pages.audience.operators.date.relatives.gt').toLowerCase()} ${value} ${dayUnit})`
      }
      if (dateFilter.operator === 'lte') {
        return ` (${t('pages.audience.operators.date.relatives.lte').toLowerCase()} ${value} ${dayUnit})`
      }
      if (dateFilter.operator === 'eq') {
        return ` (${t('pages.audience.operators.date.relatives.eq').toLowerCase()} ${value} ${dayUnit})`
      }
      return ` (depuis ${value} ${dayUnit})`
    }

    return ''
  }

  const formatRule = (ruleKey: Rules, rule: AudienceRule): string => {
    const ruleName = formatRuleName(ruleKey)

    const timeRules: Rules[] = ['registrationDate', 'lastValidCheckin', 'age']
    const isTimeRule = timeRules.includes(ruleKey)

    const operator = formatOperator(rule.operator, isTimeRule)
    const value = formatValue(rule.value, ruleKey, rule.operator)
    const numericValue = Array.isArray(rule.value) ? Number(rule.value[0]) : Number(rule.value)
    const unit = formatTimeUnit(ruleKey, numericValue)

    let formattedValue = value
    if (unit) {
      formattedValue = `${value} ${unit}`
    }

    let dateFilterText = ''
    if (rule.parameters?.date_filter) {
      dateFilterText = formatDateFilter(rule.parameters.date_filter)
    }

    return `${ruleName} : ${operator} ${formattedValue}${dateFilterText}`
  }

  const formatRules = (rules: Partial<Record<Rules, AudienceRule>>): string[] => {
    const formattedRules: string[] = []

    for (const [key, rule] of Object.entries(rules)) {
      if (rule && rule.value !== null && rule.value !== undefined) {
        if (Array.isArray(rule.value) && rule.value.length === 0) {
          continue
        }
        formattedRules.push(formatRule(key as Rules, rule))
      }
    }

    return formattedRules
  }

  return {
    formatRules
  }
}
